/**
 * PrizeDrawStyleGallerySpotlight section renderers for GridEngine
 * Full-bleed carousel hero with countdown overlay and prize gallery
 */
import React, { useState, useEffect, useMemo } from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import DrawCountdownHeader from '../../../components/DrawCountdownHeader'
import PrizeGallery from '../../../components/PrizeGallery'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useTranslations } from '../../../contexts/TranslationContext'

/**
 * Hero Section - Full-bleed carousel of prize images with countdown overlay
 */
export function PrizeDrawGalleryHeroSection({ 
  mainDraw, 
  editMode, 
  handleTranslationUpdate, 
  onLoginClick,
  timeLeft,
  shouldAnimate,
  countdownStyle,
  prizes
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const [activeIndex, setActiveIndex] = useState(0)

  const rawPrizes = prizes || mainDraw?.prizes || []
  // Only prizes that have images participate in the spotlight carousel
  const carouselPrizes = useMemo(
    () => rawPrizes.filter(p => p?.image_url),
    [rawPrizes]
  )

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : ''

  // Auto-advance carousel every 5.5s (pause when only 1 prize)
  useEffect(() => {
    if (carouselPrizes.length < 2) return undefined
    const id = setInterval(() => {
      setActiveIndex(i => (i + 1) % carouselPrizes.length)
    }, 5500)
    return () => clearInterval(id)
  }, [carouselPrizes.length])

  // Keep activeIndex in range if prizes change
  useEffect(() => {
    if (activeIndex >= carouselPrizes.length && carouselPrizes.length > 0) {
      setActiveIndex(0)
    }
  }, [carouselPrizes.length, activeIndex])

  const goPrev = () => setActiveIndex(i => (i - 1 + carouselPrizes.length) % carouselPrizes.length)
  const goNext = () => setActiveIndex(i => (i + 1) % carouselPrizes.length)
  const activePrize = carouselPrizes[activeIndex]

  // Floating countdown card
  const CountdownFloatCard = () => {
    if (!mainDraw?.draw_date) return null
    return (
      <div
        className="rounded-2xl p-4 sm:p-5 backdrop-blur-md border shadow-2xl"
        style={{
          backgroundColor: 'color-mix(in oklab, var(--charity-primary) 80%, transparent)',
          borderColor: 'color-mix(in oklab, var(--charity-primary-contrast) 35%, transparent)',
          color: 'var(--charity-primary-contrast)',
        }}
      >
        <DrawCountdownHeader
          draw={mainDraw}
          className="mb-2"
          labelClassName="opacity-90"
          labelStyle={{ color: 'var(--charity-primary-contrast)' }}
        >
          <InlineTranslationEdit
            translationKey="prizeGallerySpotlight.drawIn"
            value={c('prizeGallerySpotlight.drawIn', 'Draw In')}
            fallback="Draw In"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </DrawCountdownHeader>
        {timeLeft && (
          <CountdownTimerStyles timeLeft={timeLeft} shouldAnimate={shouldAnimate} style={countdownStyle} />
        )}
      </div>
    )
  }

  if (carouselPrizes.length === 0) {
    // Fallback when no prizes with images
    return (
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <HeroSiteLogo tone="dark" />
          <div className="text-center">
            <Gift className="w-16 h-16 mx-auto mb-4 opacity-80" />
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <InlineTranslationEdit
                translationKey="prizeGallerySpotlight.fallbackTitle"
                value={c('prizeGallerySpotlight.fallbackTitle', mainDraw?.title || 'Prize Draw')}
                fallback="Prize Draw"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              <InlineTranslationEdit
                translationKey="prizeGallerySpotlight.fallbackSubtitle"
                value={c('prizeGallerySpotlight.fallbackSubtitle', 'Enter for your chance to win amazing prizes!')}
                fallback="Enter for your chance to win amazing prizes!"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
            <HeroPartnerGlassSelect className="mx-auto mb-8 w-full max-w-md" />
            <button
              onClick={() => purchaseNavigate('/purchase')}
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-purple-700 text-xl font-black rounded-2xl shadow-2xl transition-all hover:scale-105"
            >
              <Ticket className="w-6 h-6" />
              <InlineTranslationEdit
                translationKey="prizeGallerySpotlight.enterNow"
                value={c('prizeGallerySpotlight.enterNow', 'ENTER NOW')}
                fallback="ENTER NOW"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </button>
            <div className="mt-8 max-w-md mx-auto">
              <CountdownFloatCard />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <HeroSiteLogo tone="dark" />
      <div className="relative w-full bg-gray-950 overflow-hidden">
        {/* Full-bleed image stage.
            NOTE: must include `w-full` on this aspect-ratio container. Without
            an explicit width, `aspect-[16/9]` + `min-h-[360px]` makes height
            definite, so CSS computes width FROM height via the ratio
            (360 × 16/9 = 640px) — overflowing a 393px mobile viewport and
            pushing the floating countdown + caption off-screen. With `w-full`
            the width is definite (378px), so aspect-ratio resolves height from
            width and min-height clamps it without growing width. */}
        <div className="relative w-full aspect-[16/9] min-h-[360px] sm:aspect-[21/9] sm:min-h-[460px] lg:min-h-[560px]">
          {carouselPrizes.map((prize, i) => (
            <div
              key={prize.id ?? i}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${
                i === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
              aria-hidden={i !== activeIndex}
            >
              <img
                src={prize.image_url}
                alt={prize.name || 'Prize'}
                className="w-full h-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
              />
              {/* Bottom gradient for caption legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent pointer-events-none" />
            </div>
          ))}

          {/* Floating countdown (top-right) */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 w-56 sm:w-64 max-w-[70%]">
            <CountdownFloatCard />
          </div>

          {/* Caption overlay (bottom-left) */}
          {activePrize && (
            <div className="absolute bottom-0 left-0 right-0 z-20 p-4 sm:p-8 lg:p-12">
              <div className="max-w-3xl">
                <span
                  className="inline-block px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3"
                  style={{
                    backgroundColor: 'var(--charity-primary)',
                    color: 'var(--charity-primary-contrast)',
                  }}
                >
                  {activePrize.tier ? `${activePrize.tier} prize` : (
                    <InlineTranslationEdit
                      translationKey="prizeGallerySpotlight.featured"
                      value={c('prizeGallerySpotlight.featured', 'Featured Prize')}
                      fallback="Featured Prize"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  )}
                </span>
                <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-2 leading-tight drop-shadow-lg">
                  {activePrize.name || 'Prize'}
                </h1>
                <div className="flex flex-wrap items-center gap-4">
                  {activePrize.value != null && (
                    <span className="text-xl sm:text-2xl font-bold text-white">
                      ${Number(activePrize.value).toLocaleString()}
                    </span>
                  )}
                  {activePrize.description && (
                    <p className="text-sm sm:text-base text-white/85 max-w-xl line-clamp-2">
                      {activePrize.description}
                    </p>
                  )}
                </div>

                <HeroPartnerGlassSelect className="mt-4 w-full max-w-md" />

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => purchaseNavigate('/purchase')}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold shadow-lg transition hover:scale-105 bg-white"
                    style={{ color: 'var(--charity-primary)' }}
                  >
                    <Ticket className="w-5 h-5" />
                    <InlineTranslationEdit
                      translationKey="prizeGallerySpotlight.enterToWin"
                      value={c('prizeGallerySpotlight.enterToWin', 'Enter To Win')}
                      fallback="Enter To Win"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Prev/Next navigation arrows — desktop only */}
          {carouselPrizes.length > 1 && (
            <>
              <button
                onClick={goPrev}
                aria-label={tr('prizeGallerySpotlight.previousPrize', 'Previous prize')}
                className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white transition"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goNext}
                aria-label={tr('prizeGallerySpotlight.nextPrize', 'Next prize')}
                className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white transition"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Mobile dots */}
              <div className="sm:hidden absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                {carouselPrizes.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    aria-label={tr('prizeGallerySpotlight.goToPrize', 'Go to prize {count}').replace('{count}', i + 1)}
                    className="w-2 h-2 rounded-full transition"
                    style={{
                      backgroundColor:
                        i === activeIndex
                          ? '#fff'
                          : 'rgba(255,255,255,0.40)',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {carouselPrizes.length > 1 && (
          <div className="bg-gray-900 dark:bg-gray-950 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto">
              <div className="flex gap-3 min-w-max">
                {carouselPrizes.map((prize, i) => {
                  const isActive = i === activeIndex
                  return (
                    <button
                      key={prize.id ?? i}
                      onClick={() => setActiveIndex(i)}
                      className="group relative flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden transition-all"
                      style={{
                        outline: isActive ? '3px solid var(--charity-primary)' : '3px solid transparent',
                        outlineOffset: '2px',
                        opacity: isActive ? 1 : 0.6,
                      }}
                      aria-label={tr('prizeGallerySpotlight.showPrize', 'Show {prize}')
                        .replace('{prize}', prize.name || tr('prizeGallerySpotlight.prizeNumber', 'prize {count}').replace('{count}', i + 1))}
                    >
                      <img
                        src={prize.image_url}
                        alt={prize.name || tr('prizeGallerySpotlight.prizeNumber', 'prize {count}').replace('{count}', i + 1)}
                        className="w-full h-full object-cover transition group-hover:scale-105"
                        loading="lazy"
                      />
                      {!isActive && (
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Jackpot Summary Section - Prize gallery with all prizes
 */
export function PrizeDrawGalleryJackpotSummarySection({ prizes, editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const rawPrizes = prizes || []
  if (rawPrizes.length === 0) return null

  return (
    <div className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <PrizeGallery
          prizes={rawPrizes}
          title={c('prizeGallerySpotlight.galleryTitle', 'All Prizes')}
        />
      </div>
    </div>
  )
}

/**
 * Final CTA Section - Call-to-action to buy tickets
 */
export function PrizeDrawGalleryFinalCTASection({ editMode, handleTranslationUpdate, onLoginClick }) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)

  const heroGradientStyle = {
    backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
    color: '#fff',
  }

  return (
    <div className="py-16 relative overflow-hidden" style={heroGradientStyle}>
      <div className="absolute inset-0 bg-black/10 pointer-events-none" aria-hidden="true" />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ color: '#fff' }}>
          <InlineTranslationEdit
            translationKey="prizeGallerySpotlight.finalCtaTitle"
            value={c('prizeGallerySpotlight.finalCtaTitle', "Don't Miss Your Chance")}
            fallback="Don't Miss Your Chance"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto" style={{ color: '#fff' }}>
          <InlineTranslationEdit
            translationKey="prizeGallerySpotlight.finalCtaDesc"
            value={c('prizeGallerySpotlight.finalCtaDesc', 'Enter now to win one of these incredible prizes.')}
            fallback="Enter now to win one of these incredible prizes."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="inline-flex items-center gap-3 px-10 py-5 bg-white text-xl font-black rounded-2xl hover:scale-105 transition-all shadow-2xl"
          style={{ color: 'var(--charity-primary)' }}
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit
            translationKey="prizeGallerySpotlight.finalCtaButton"
            value={c('prizeGallerySpotlight.finalCtaButton', 'BUY TICKETS')}
            fallback="BUY TICKETS"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </div>
  )
}
